import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, ScrollView,
  Dimensions, Image, StyleSheet,
  Keyboard, Animated, Platform,
} from 'react-native';
import {
  Paragraph, Button, Dialog, Portal, TextInput,
  List, Searchbar, Checkbox,
} from 'react-native-paper';
import * as ImagePicker from 'expo-image-picker';

const styles = StyleSheet.create({
  imageContainer: {
    alignItems: 'center',
    marginVertical: 10,
  },
  image: {
    width: 120,
    height: 120,
    borderRadius: 8,
  },
  imageButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
    marginTop: 10,
  },
  sectionLabel: {
    fontWeight: '600',
    marginTop: 10,
    marginBottom: 5,
  },
});

function BagDialogs({
  dialogVisible,
  hideDialog,
  currentBag,
  name,
  setName,
  depositAmount,
  setDepositAmount,
  handleSave,
  rentDialogVisible,
  hideRentDialog,
  searchQuery,
  setSearchQuery,
  usersData,
  season,
  selectedUser,
  setSelectedUser,
  handleRent,
  deleteDialogVisible,
  hideDeleteDialog,
  confirmDelete,
  returnDialogVisible,
  hideReturnDialog,
  depositReturned,
  setDepositReturned,
  returnComment,
  setReturnComment,
  returnPicture,
  setReturnPicture,
  handleReturn,
  rentalComment,
  setRentalComment,
  rentalPicture,
  setRentalPicture,
}) {
  const [selectionError, setSelectionError] = useState(null);
  const shift = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      return () => {};
    }
    const keyboardWillShow = Keyboard.addListener('keyboardWillShow', (e) => {
      Animated.timing(shift, {
        toValue: -(e.endCoordinates.height / 2),
        duration: e.duration,
        useNativeDriver: true,
      }).start();
    });

    const keyboardWillHide = Keyboard.addListener('keyboardWillHide', (e) => {
      Animated.timing(shift, {
        toValue: 0,
        duration: e.duration,
        useNativeDriver: true,
      }).start();
    });

    return () => {
      keyboardWillShow.remove();
      keyboardWillHide.remove();
    };
  }, [shift]);

  const handleChoosePhoto = async (setPicture) => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 0.7,
      base64: true,
    });
    if (!result.canceled) {
      setPicture({ uri: `data:${result.assets[0].mimeType};base64,${result.assets[0].base64}` });
    }
  };

  const handleTakePhoto = async (setPicture) => {
    const result = await ImagePicker.launchCameraAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 0.7,
      base64: true,
    });
    if (!result.canceled) {
      setPicture({ uri: `data:${result.assets[0].mimeType};base64,${result.assets[0].base64}` });
    }
  };

  const hasSecurityDeposit = (user) => !season
    || user.securityDeposits?.some((d) => d.seasonId === Number(season.id));

  const handleUserSelection = (user) => {
    const hasActiveRental = user.rentals.some((rental) => !rental.returnDate);
    if (hasActiveRental) {
      setSelectionError('Cet utilisateur a déjà une location en cours.');
    } else if (!hasSecurityDeposit(user)) {
      setSelectionError(
        "La caution de cet utilisateur n'a pas été validée pour la saison en cours.",
      );
    } else {
      setSelectionError(null);
    }
    setSelectedUser(user);
  };

  const internalHideRentDialog = () => {
    setSelectionError(null);
    setRentalComment('');
    setRentalPicture(null);
    hideRentDialog();
  };

  const internalHideReturnDialog = () => {
    setReturnComment('');
    setReturnPicture(null);
    hideReturnDialog();
  };

  return (
    <Portal>
      <Animated.View style={{ flex: 1, transform: [{ translateY: shift }] }} pointerEvents="box-none">
        <Dialog visible={dialogVisible} onDismiss={hideDialog}>
          <Dialog.Title>{currentBag ? 'Modifier Sac' : 'Ajouter Sac'}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Nom du sac"
              value={name}
              onChangeText={setName}
              style={{ marginBottom: 10 }}
            />
            <TextInput
              label="Montant de la caution"
              value={depositAmount}
              onChangeText={setDepositAmount}
              keyboardType="numeric"
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={hideDialog}>Annuler</Button>
            <Button onPress={handleSave}>Enregistrer</Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog visible={rentDialogVisible} onDismiss={internalHideRentDialog}>
          <Dialog.Title>Louer à</Dialog.Title>
          <Dialog.ScrollArea style={{ maxHeight: 0.7 * Dimensions.get('window').height }}>
            <ScrollView>
              <Searchbar
                placeholder="Rechercher un utilisateur"
                onChangeText={(value) => setSearchQuery(value?.toLowerCase())}
                value={searchQuery}
              />
              {selectionError && <Text style={{ color: 'red', padding: 10 }}>{selectionError}</Text>}
              {usersData?.allUsers.filter((u) => {
                const matchSearchBar = u.firstName?.toLowerCase().includes(searchQuery)
                || u.lastName?.toLowerCase().includes(searchQuery);
                return matchSearchBar;
              }).map((item) => (
                <List.Item
                  key={item.id.toString()}
                  title={`${item.firstName} ${item.lastName}`}
                  description={hasSecurityDeposit(item) ? 'Caution équipement payée' : 'Caution équipement non payée'}
                  descriptionStyle={{ color: hasSecurityDeposit(item) ? '#2e7d32' : '#c62828' }}
                  onPress={() => handleUserSelection(item)}
                  left={() => (
                    <List.Icon
                      icon={selectedUser?.id === item.id ? 'check-circle' : 'circle-outline'}
                      color={selectedUser?.id === item.id ? 'green' : 'gray'}
                    />
                  )}
                />
              ))}

              <Text style={styles.sectionLabel}>Commentaire (optionnel)</Text>
              <TextInput
                label="Commentaire"
                value={rentalComment}
                onChangeText={setRentalComment}
                multiline
                numberOfLines={3}
                style={{ marginBottom: 10 }}
              />

              <Text style={styles.sectionLabel}>Photo du contenu (optionnel)</Text>
              <View style={styles.imageContainer}>
                {rentalPicture ? (
                  <Image source={rentalPicture} style={styles.image} />
                ) : (
                  <View style={[styles.image, { backgroundColor: '#f0f0f0', justifyContent: 'center', alignItems: 'center' }]}>
                    <Text style={{ color: '#999' }}>Aucune photo</Text>
                  </View>
                )}
                <View style={styles.imageButtons}>
                  <Button icon="image-album" onPress={() => handleChoosePhoto(setRentalPicture)} compact>Galerie</Button>
                  <Button icon="camera" onPress={() => handleTakePhoto(setRentalPicture)} compact>Photo</Button>
                  {rentalPicture && (
                  <Button icon="delete" onPress={() => setRentalPicture(null)} compact textColor="red">Suppr.</Button>
                  )}
                </View>
              </View>
            </ScrollView>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={internalHideRentDialog}>Annuler</Button>
            <Button onPress={handleRent} disabled={!selectedUser || !!selectionError}>Louer</Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog visible={deleteDialogVisible} onDismiss={hideDeleteDialog}>
          <Dialog.Title>Supprimer Sac</Dialog.Title>
          <Dialog.Content>
            <Paragraph>Etes vous certain de vouloir supprimer ce sac ?</Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={hideDeleteDialog}>Annuler</Button>
            <Button onPress={confirmDelete}>Supprimer</Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog visible={returnDialogVisible} onDismiss={internalHideReturnDialog}>
          <Dialog.Title>Effectuer un retour</Dialog.Title>
          <Dialog.ScrollArea style={{ maxHeight: 0.6 * Dimensions.get('window').height }}>
            <ScrollView>
              <Paragraph>
                Voulez-vous confirmer le retour de ce sac par
                {' '}
                {currentBag?.rentals.find((r) => !r.returnDate)?.user.firstName}
                {' '}
                {currentBag?.rentals.find((r) => !r.returnDate)?.user.lastName}
              </Paragraph>

              <Text style={styles.sectionLabel}>Commentaire (optionnel)</Text>
              <TextInput
                label="Commentaire"
                value={returnComment}
                onChangeText={setReturnComment}
                style={{ marginBottom: 10 }}
                multiline
                numberOfLines={3}
              />

              <Text style={styles.sectionLabel}>Photo du contenu (optionnel)</Text>
              <View style={styles.imageContainer}>
                {returnPicture ? (
                  <Image source={returnPicture} style={styles.image} />
                ) : (
                  <View style={[styles.image, { backgroundColor: '#f0f0f0', justifyContent: 'center', alignItems: 'center' }]}>
                    <Text style={{ color: '#999' }}>Aucune photo</Text>
                  </View>
                )}
                <View style={styles.imageButtons}>
                  <Button icon="image-album" onPress={() => handleChoosePhoto(setReturnPicture)} compact>Galerie</Button>
                  <Button icon="camera" onPress={() => handleTakePhoto(setReturnPicture)} compact>Photo</Button>
                  {returnPicture && (
                  <Button icon="delete" onPress={() => setReturnPicture(null)} compact textColor="red">Suppr.</Button>
                  )}
                </View>
              </View>

              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10 }}>
                <Checkbox
                  status={depositReturned ? 'checked' : 'unchecked'}
                  onPress={() => {
                    setDepositReturned(!depositReturned);
                  }}
                />
                <Text>Caution retournée ?</Text>
              </View>
            </ScrollView>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={internalHideReturnDialog}>Annuler</Button>
            <Button onPress={handleReturn}>Confirmer</Button>
          </Dialog.Actions>
        </Dialog>
      </Animated.View>
    </Portal>
  );
}

export default BagDialogs;
